import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import toast from '../utils/toast';

const TransferAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [targetSection, setTargetSection] = useState('tickets');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid transfer link. No token provided.');
      setLoading(false);
      return;
    }

    // If user is not authenticated, redirect to login with return URL
    // The login page preserves the redirect param when switching to signup,
    // so new users can register and still complete the transfer
    if (!isAuthenticated) {
      const returnUrl = `/transfer/accept?token=${token}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    setLoading(false);
  }, [token, isAuthenticated, navigate]);

  const handleAcceptTransfer = async () => {
    if (!token) return;
    
    setAccepting(true);
    setError(null);

    try {
      const response = await api.acceptTransfer(token);
      
      if (response?.success) {
        setSuccess(true);
        setTicket(response.data);
        toast.success('Ticket transfer accepted! The ticket is now yours.');
        
        // Determine which dashboard tab to navigate to based on ticket type
        const typeName = (response.data?.ticket_type_name || '').toLowerCase();
        let section = 'tickets'; // default to festival tickets
        if (typeName.includes('amphitheater') || typeName.includes('pacific')) {
          section = 'amphitheater-tickets';
        } else if (typeName.includes('vendor') || typeName.includes('booth')) {
          section = 'vendor-tickets';
        }
        // Store for the "View My Tickets" button
        setTargetSection(section);
      } else {
        setError(response?.error?.message || 'Failed to accept transfer');
      }
    } catch (err) {
      setError(err.message || 'Failed to accept transfer. The link may have expired.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AnnouncementBar />
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          padding: '2rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>⏳</div>
            <h2>Loading transfer details...</h2>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !success) {
    return (
      <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AnnouncementBar />
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          padding: '2rem'
        }}>
          <div style={{ 
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '3rem',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '1rem' }}>❌</div>
            <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>Transfer Failed</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AnnouncementBar />
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          padding: '2rem'
        }}>
          <div style={{ 
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '3rem',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '1rem' }}>🎉</div>
            <h2 style={{ color: '#22c55e', marginBottom: '1rem' }}>Ticket Received!</h2>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              The ticket has been successfully transferred to your account.
            </p>
            {ticket && (
              <div style={{ 
                backgroundColor: '#f0fdf4', 
                padding: '1rem', 
                borderRadius: '8px',
                marginBottom: '2rem',
                border: '1px solid #22c55e'
              }}>
                <p style={{ fontWeight: '600', color: '#166534' }}>
                  {ticket.ticket_type_name || 'Festival Ticket'}
                </p>
                <p style={{ color: '#166534', fontSize: '14px' }}>
                  Code: {ticket.ticket_code}
                </p>
              </div>
            )}
            <button
              onClick={() => navigate(`/dashboard?section=${targetSection}`)}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              View My Tickets
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show accept confirmation
  return (
    <div className="page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AnnouncementBar />
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        padding: '2rem'
      }}>
        <div style={{ 
          textAlign: 'center',
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '1rem' }}>🎫</div>
          <h2 style={{ marginBottom: '1rem' }}>Accept Ticket Transfer</h2>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Someone has sent you a ticket to OC MENA Festival!
          </p>
          <p style={{ color: '#666', marginBottom: '2rem', fontSize: '14px' }}>
            Logged in as: <strong>{user?.email}</strong>
          </p>
          
          {error && (
            <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</p>
          )}
          
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAcceptTransfer}
              disabled={accepting}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: accepting ? '#9ca3af' : '#9333ea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: accepting ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              {accepting ? 'Accepting...' : 'Accept Ticket'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TransferAccept;
